import { useRef } from 'react';

import { useAtomValue } from 'jotai';

import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { ukLocale } from '@core/Utils/Intl/MappedLocales';
import { Button } from '@gds/Button/Button';
import { Input } from '@gds/Input/Input';
import { Slider } from '@gds/Slider/Slider';
import { Stepper } from '@gds/Stepper/Stepper';
import { Typography } from '@gds/Typography/Typography';
import { useRangeFilters } from '@search-filters/Hooks/UseRangeFilters';

import type { FilterControlBaseProps } from '../../FilterPanel.entity';
import { RANGE } from './MileageFilter.constants';

import piStyles from '../PairedInputs/PairedInputs.module.css';
import styles from './MileageFilter.module.css';

type MileageFilterProps = FilterControlBaseProps;

export const MileageFilter = ({ strings, tenant }: MileageFilterProps) => {
  const minVal = RANGE[0].toString();
  const maxVal = RANGE[RANGE.length - 1].toString();
  const { totalCount } = useAtomValue(aggregationsAtom);

  const { gteInputProps, lteInputProps, sliderProps, canReset, reset, stepGte, stepLte } =
    useRangeFilters('mileage', RANGE, [minVal, maxVal]);

  const gteInputRef = useRef<HTMLInputElement>(null);
  const lteInputRef = useRef<HTMLInputElement>(null);

  return (
    <section className={styles.mileageFilter}>
      <Typography className={styles.infoLine} variant="subheading4">
        {strings.mileageInfoLine}
      </Typography>
      <div className={piStyles.pairedInputs}>
        <Input
          {...gteInputProps}
          ref={gteInputRef}
          label={strings.min}
          type="number"
          min={0}
          rightAddons={
            <Stepper
              label="minimum mileage"
              focusTargetRef={gteInputRef}
              onIncrement={() => stepGte(1)}
              onDecrement={() => stepGte(-1)}
            />
          }
          className={styles.rangeInput}
          rightAddonsClassName={styles.stepperWrapper}
          wrapperClassName={styles.rangeInputControl}
          labelPlacement={tenant === 'rac' ? 'floating' : 'above'}
        />
        <Input
          {...lteInputProps}
          ref={lteInputRef}
          label={strings.max}
          type="number"
          max={maxVal}
          rightAddons={
            <Stepper
              label="maximum mileage"
              focusTargetRef={lteInputRef}
              onIncrement={() => stepLte(1)}
              onDecrement={() => stepLte(-1)}
            />
          }
          className={styles.rangeInput}
          rightAddonsClassName={styles.stepperWrapper}
          wrapperClassName={styles.rangeInputControl}
          labelPlacement={tenant === 'rac' ? 'floating' : 'above'}
        />
      </div>
      <div className={styles.auxInfo}>
        <Typography Component="span" className={styles.resultCount} variant="caption2">
          ( {totalCount.toLocaleString(ukLocale)})
        </Typography>
        <Button
          color="secondary"
          disabled={!canReset()}
          onClick={() => reset()}
          size="small"
          variant="link"
        >
          {strings.reset}
        </Button>
      </div>
      <Slider {...sliderProps} className={styles.priceRangeSlider} dataTestId="mileage-slider" />
    </section>
  );
};
